import { memo, type MutableRefObject, useCallback, useMemo } from 'react';
import type { GenericPointFeature, LngLat, YMap } from '@yandex/ymaps3-types';
import { Label, Text, VStack } from '@direct-frontend/components';
import type { Nullable, Optional } from '@direct-frontend/stdlib';
import { useYandexMapsApi } from '@adv-frontend/yandex-maps-kit';
import { type PickupPoint } from '@/shared/api/pickupPoint';
import { formatDeliveryDate } from '@/shared/lib/datetime';
import { pluralize } from '@/shared/lib/pluralize';
import { getClusterScale } from './lib/getClusterScale';
import { useClusterMenu } from './lib/hooks/useClusterMenu';
import { MAX_CLUSTER_ZOOM, useMapViewport, Z_INDEX_CLUSTERS, Z_INDEX_PINS } from '../../lib';
import { usePickupAddressContext } from '../../PickupAddressContext';
import { PickupAddressMapClusterMenu } from '../PickupAddressMapClusterMenu';
import { PickupAddressMapPin } from '../PickupAddressMapPin';
import styles from './PickupAddressMapCluster.module.css';

interface PinBadgeInterface {
    point: PickupPoint;
    isSelected: boolean;
}

const PinBadge = (props: PinBadgeInterface) => {
    const { allItemsCount, type } = usePickupAddressContext();
    const { point, isSelected } = props;

    const showSelfPickupHint = Boolean(
        type === 'self' && point?.itemsCount && !isSelected && !(point.itemsCount === 1 && allItemsCount === 1)
    );

    const date = point.deliveryInterval && formatDeliveryDate(point.deliveryInterval);
    const isEqualCount = allItemsCount === point?.itemsCount;

    if (showSelfPickupHint && allItemsCount) {
        return (
            <div className={styles.badge}>
                <VStack gap="4">
                    {date && <Text size="xs">{date}</Text>}

                    <Label color={isEqualCount ? 'success' : 'message'}>
                        {point.itemsCount} из {pluralize(allItemsCount, '%d товара', '%d товаров', '%d товаров')}
                    </Label>
                </VStack>
            </div>
        );
    }

    return null;
};

interface PickupAddressMapClusterProps {
    ymapRef: MutableRefObject<Nullable<YMap>>;
    sourceId: string;
    onUpdatePosition: (position: LngLat, zoomDelta?: number) => void;
    setPickupPontClustered: (isClustered: boolean) => void;
    gridSize: Optional<number>;
}

export const PickupAddressMapCluster = memo(function PickupAddressMapCluster(props: PickupAddressMapClusterProps) {
    const mapsApi = useYandexMapsApi();
    const { sourceId, onUpdatePosition, setPickupPontClustered, gridSize = 128, ymapRef } = props;
    const { pickupPoint, pickupPoints, onSelectPickupPoint } = usePickupAddressContext();

    const { getMapZoom } = useMapViewport({ ymapRef });
    const { handleChangeMenu, handleCloseMenu, menuCoordinates, menuPoints } = useClusterMenu();

    const handleSelectPoint = useCallback(
        (point: PickupPoint, coordinates: LngLat) => {
            onSelectPickupPoint?.(point);
            onUpdatePosition(coordinates);
        },
        [onSelectPickupPoint, onUpdatePosition]
    );

    const marker = useCallback(
        (feature: GenericPointFeature<LngLat>) => {
            const currentPoint = pickupPoints.find((p) => p.id === feature.id) ?? null;
            const isSelected = pickupPoint?.id === feature.id;

            if (isSelected) {
                setPickupPontClustered(false);
            }

            if (!mapsApi.maps || !currentPoint) {
                return <div />;
            }

            return (
                <mapsApi.maps.YMapMarker
                    zIndex={Z_INDEX_PINS}
                    source={sourceId}
                    coordinates={feature.geometry.coordinates}
                    onClick={() => handleSelectPoint(currentPoint, feature.geometry.coordinates)}
                >
                    <PickupAddressMapPin
                        point={currentPoint}
                        isSelected={isSelected}
                        name={feature.properties?.name?.toString() ?? ''}
                    >
                        <PinBadge point={currentPoint} isSelected={isSelected} />
                    </PickupAddressMapPin>
                </mapsApi.maps.YMapMarker>
            );
        },
        [pickupPoints, pickupPoint?.id, mapsApi, sourceId, setPickupPontClustered, handleSelectPoint]
    );

    const cluster = useCallback(
        (coordinates: LngLat, features: GenericPointFeature<LngLat>[]) => {
            if (!mapsApi.maps) {
                return <div />;
            }

            const currentPoint = features.find((feature) => pickupPoint?.id === feature.id) ?? null;

            if (currentPoint) {
                setPickupPontClustered(true);
            }

            return (
                <mapsApi.maps.YMapMarker
                    zIndex={Z_INDEX_CLUSTERS}
                    source={sourceId}
                    coordinates={coordinates}
                    onClick={() => {
                        const zoom = getMapZoom();

                        if (zoom && zoom >= MAX_CLUSTER_ZOOM) {
                            handleChangeMenu(features);

                            return;
                        }

                        onUpdatePosition(coordinates, 2);
                    }}
                >
                    <div
                        className={styles.cluster}
                        style={{ transform: `translate(-50%, -50%) scale(${getClusterScale(features.length)}` }}
                    >
                        {features.length}
                    </div>
                </mapsApi.maps.YMapMarker>
            );
        },
        [mapsApi, sourceId, pickupPoint?.id, setPickupPontClustered, getMapZoom, onUpdatePosition, handleChangeMenu]
    );

    const features: GenericPointFeature<LngLat>[] = useMemo(
        () =>
            pickupPoints.map((p) => {
                return {
                    id: p.id,
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [p.position.longitude, p.position.latitude],
                    },
                };
            }),
        [pickupPoints]
    );

    if (!mapsApi.addons?.clusterer?.YMapClusterer) {
        return null;
    }

    return (
        <>
            <mapsApi.addons.clusterer.YMapClusterer
                method={mapsApi.addons.clusterer.clusterByGrid({ gridSize })}
                features={features}
                marker={marker}
                cluster={cluster}
            />

            {menuCoordinates && menuPoints && (
                <PickupAddressMapClusterMenu
                    sourceId={sourceId}
                    ymapRef={ymapRef}
                    coordinates={menuCoordinates}
                    points={menuPoints}
                    onOptionClick={handleSelectPoint}
                    onClose={handleCloseMenu}
                />
            )}
        </>
    );
});
